import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["aria-labelledby", "children", "disabled", "error", "Fieldset", "gap", "help", "inline", "label", "required", "tooltip", "tooltipPlacement", "use", "validationMessage"],
  _excluded2 = ["gap"];
import { Children, cloneElement, isValidElement } from 'react';
import { Fieldset } from '../Fieldset';
import styled, { useTheme } from 'foundations-theming/utils';
import { warnIfFragment } from '../../utils/warnIfFragment/warnIfFragment';
import { uniqueId } from '../../utils/uniqueId';
import { FieldsetLegend, StyledSpan } from '../Fieldset/Fieldset';
import { StyledFormControlWrapper } from '../FormControl/FormControl';
import { InputGroupContext, useInputGroupContext } from './InputGroupProvider';
import { deepMerge } from '../../utils/deepMerge';
import devLogger from 'react-utils/devLogger';
import getComponentName from 'react-utils/getComponentName';
import { MERCURY_LAYER, VENUS_LAYER } from '../../constants';
import { filterProps } from '../../../utils/filterProps';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getLengthValue = (length, theme) => {
  let computedLength;
  if (typeof length === 'boolean') {
    computedLength = length ? theme.space['200'] : false;
  }
  if (typeof length === 'string') {
    computedLength = length;
  }
  if (typeof length === 'number') {
    computedLength = `${parseInt(length.toString(), 10)}px`;
  }
  return computedLength || false;
};
const StyledText = styled.small.withConfig({
  displayName: "InputGroup__StyledText"
})(({
  theme
}) => {
  return {
    color: theme.color['text-alert-default'],
    fontWeight: theme.typography['heading-200'].fontWeight,
    display: 'block',
    marginTop: theme.space['100']
  };
});
const formGroupMemberMediaBodyStyles = {
  flex: '1 1 0%',
  minWidth: 0
};
const InputGroupWrapper = styled.div.withConfig({
  displayName: "InputGroup__InputGroupWrapper"
})(({
  $inline,
  $gap,
  $use,
  $error,
  theme
}) => {
  return {
    display: $inline ? 'inline-flex' : 'flex',
    '& > *': Object.assign({
      '[id^="FormControl"]': {
        position: $error ? 'relative' : undefined,
        zIndex: $error ? MERCURY_LAYER : undefined,
        '&:focus': {
          position: 'relative',
          zIndex: VENUS_LAYER
        }
      }
    }, $use === 'itemBoth' && {
      '&:not(:first-child):not(:last-child)': {
        formGroupMemberMediaBodyStyles
      }
    }, $use === 'flex' && formGroupMemberMediaBodyStyles),
    [`& ${StyledFormControlWrapper}`]: {
      // focused input should have a higher z-index than other inputs to allow focus ring / user defined borders to show
      // Note: at parity with UIInputGroup
      '& input:focus': {
        position: 'relative',
        zIndex: VENUS_LAYER
      },
      '&:not(&:only-child)': Object.assign({}, !$gap && {
        '&:first-of-type': {
          '& input': {
            borderStartEndRadius: theme.borderRadius['0'],
            borderEndEndRadius: theme.borderRadius['0']
          }
        },
        '&:last-of-type': {
          '& input': {
            borderStartStartRadius: theme.borderRadius['0'],
            borderEndStartRadius: theme.borderRadius['0']
          }
        },
        '&:not(&:first-of-type):not(&:last-of-type)': {
          '& input': {
            borderStartStartRadius: theme.borderRadius['0'],
            borderStartEndRadius: theme.borderRadius['0'],
            borderEndEndRadius: theme.borderRadius['0'],
            borderEndStartRadius: theme.borderRadius['0']
          }
        }
      })
    },
    '& > :not(:first-child)': Object.assign({}, $gap === false && {
      '[id^="FormControl"]': {
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0
      }
    }, $use === 'itemLeft' && formGroupMemberMediaBodyStyles),
    '& > :not(:last-child)': Object.assign({}, $gap === false && {
      '[id^="FormControl"]': {
        borderBottomRightRadius: 0,
        borderTopRightRadius: 0
      }
    }, $use === 'itemRight' && formGroupMemberMediaBodyStyles),
    [`& ${StyledFormControlWrapper} + ${StyledFormControlWrapper}`]: {
      marginTop: theme.space['0'],
      marginLeft: $gap === false ? `-${theme.space['25']}` : $gap
    },
    [`& ${StyledSpan}`]: {
      fontWeight: theme.typography['heading-50'].fontWeight
    }
  };
});
const InputGroupLegend = styled(FieldsetLegend)(({
  theme
}) => {
  return {
    [`& ${StyledSpan}`]: {
      fontWeight: theme.typography['heading-50'].fontWeight
    }
  };
});
const MessageWrapper = styled.div.withConfig({
  displayName: "InputGroup__MessageWrapper"
})(() => {
  return {
    display: 'flex',
    flexDirection: 'column'
  };
});
const MessageOuter = styled.div.withConfig({
  displayName: "InputGroup__MessageOuter"
})(() => {
  return {
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'space-between'
  };
});
export const InputGroup = _ref => {
  var _inputGroupContext$fi;
  let {
      'aria-labelledby': ariaLabelledBy,
      children,
      disabled,
      error = false,
      Fieldset: CustomFieldset = Fieldset,
      gap = false,
      help,
      inline = false,
      label: legend,
      required,
      tooltip,
      tooltipPlacement = 'top',
      use = 'flex',
      validationMessage
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const legendId = `input-group-${uniqueId()}`;
  const formControlWithError = Children.toArray(children).find(child => /*#__PURE__*/isValidElement(child) && child.props && child.props.error && child.props.validationMessage);
  const computedError = error || formControlWithError && /*#__PURE__*/isValidElement(formControlWithError) && formControlWithError.props.error;
  const computedValidationMessage = validationMessage || formControlWithError && /*#__PURE__*/isValidElement(formControlWithError) && formControlWithError.props.validationMessage;
  const theme = useTheme();
  const {
    tokenProps,
    domProps,
    restProps
  } = filterProps(rest);
  // gap prop accepts different types than BaseStyleProps gap
  const tokenPropsExceptGap = _objectWithoutPropertiesLoose(tokenProps, _excluded2);
  const inputGroupContext = useInputGroupContext();
  const fieldsetStylesOverrides = deepMerge({
    fontWeight: theme.typography['heading-50'].fontWeight
  }, (_inputGroupContext$fi = inputGroupContext === null || inputGroupContext === void 0 ? void 0 : inputGroupContext.fieldsetStylesOverrides) !== null && _inputGroupContext$fi !== void 0 ? _inputGroupContext$fi : {});
  return /*#__PURE__*/_jsx(InputGroupContext.Provider, {
    value: {
      fieldsetStylesOverrides
    },
    children: /*#__PURE__*/_jsxs(CustomFieldset, Object.assign({}, domProps, restProps, tokenPropsExceptGap, {
      color: "icon-core-subtle",
      disabled: disabled,
      help: help,
      Legend: InputGroupLegend,
      legend: legend,
      legendId: legendId,
      tabIndex: 0,
      tooltip: tooltip,
      tooltipPlacement: tooltipPlacement,
      _required: required,
      children: [/*#__PURE__*/_jsx(InputGroupWrapper, {
        $error: !!error,
        $gap: getLengthValue(gap, theme),
        $inline: inline,
        $use: use,
        children: Children.map(children, child => {
          if (child == null) {
            return null;
          }
          warnIfFragment(child, 'InputGroup');
          const ariaLabel = child.props['aria-label'];
          const childAriaLabelledBy = child.props['aria-labelledby'];
          const combinedAriaLabelledBy = [ariaLabelledBy, childAriaLabelledBy].filter(Boolean).join(' ');
          if (process.env.NODE_ENV !== 'production' && getComponentName(child) === 'FormControl' && !ariaLabel && !combinedAriaLabelledBy) {
            devLogger.warn({
              message: `InputGroup: You must add an \`aria-label\` to each \`FormControl\` to describe the specific field in this input group. Alternatively, set \`FormControl[aria-labelledby]\` to the ID of an existing label element.`,
              key: `InputGroup: aria-label`,
              /* eslint-disable-next-line hubspot-dev/prefer-hubspot-url-utils */
              url: 'https://private.hubteamqa.com/foundations-components-storybook/?path=/docs/transitional-inputgroup--basic'
            });
          }
          return /*#__PURE__*/cloneElement(child, {
            'aria-labelledby': ariaLabel ? '' : legend ? `${legendId} ${child.props['aria-labelledby'] || ''}` : combinedAriaLabelledBy,
            required: child.props.required || required,
            help: null,
            tooltip: null,
            validationMessage: null
          });
        })
      }), /*#__PURE__*/_jsx(MessageOuter, {
        "aria-hidden": "true",
        children: /*#__PURE__*/_jsx(MessageWrapper, {
          "aria-hidden": "true",
          children: computedError && computedValidationMessage && /*#__PURE__*/_jsx(StyledText, {
            tabIndex: -1,
            as: "small",
            children: computedValidationMessage
          })
        })
      })]
    }))
  });
};
InputGroup.displayName = 'InputGroup';